<script setup lang="ts">
import { computed, ref, watch, type Component } from 'vue';
import { useRouter } from 'vue-router';

import type { AccessRequirementKey } from '@peanuds/app-shell';
import { formatWeaverCurrency } from '@peanuds/shared';
import { useUserAccountsStore } from '@peanuds/shared-state';

import { useTranslation } from 'i18next-vue';
import { storeToRefs } from 'pinia';

import PromoArea from './components/PromoArea.vue';
import TotalBalanceEmailVerificationDialog from './components/TotalBalanceEmailVerificationDialog.vue';
import TotalBalanceOnboardingContent from './components/TotalBalanceOnboardingContent.vue';
import TotalBalancePhoneVerificationDialog from './components/TotalBalancePhoneVerificationDialog.vue';
import { useTotalBalanceAccessViewModel } from './useTotalBalanceAccessViewModel';
import type { ActionType } from './total-balance.types';

import {
  AddPlusCircleIcon,
  ArrowLeftRightIcon,
  ArrowUpRightLgIcon,
  BankIcon,
  WalletIcon,
} from '@peanuds/ui';

interface ActionConfig {
  id: ActionType;
  labelKey: string;
  icon: Component;
  route: string;
}

const actions: ActionConfig[] = [
  {
    id: 'addMoney',
    labelKey: 'totalBalance.actions.add_money',
    icon: AddPlusCircleIcon,
    route: 'add-money',
  },
  {
    id: 'send',
    labelKey: 'totalBalance.actions.send',
    icon: ArrowUpRightLgIcon,
    route: 'send-money',
  },
  {
    id: 'iban',
    labelKey: 'totalBalance.actions.open_account',
    icon: BankIcon,
    route: 'open-account',
  },
  {
    id: 'exchange',
    labelKey: 'totalBalance.actions.exchange',
    icon: ArrowLeftRightIcon,
    route: 'accounts',
  },
];

const { t } = useTranslation();
const router = useRouter();

const userAccountsStore = useUserAccountsStore();
const { ibanAccounts } = storeToRefs(userAccountsStore);
const {
  currentOnboardingItem,
  showOnboardingContent,
  isVerificationLoading,
  isOnboarded,
  isEmailVerificationAvailable,
  handleRequirementCta,
} = useTotalBalanceAccessViewModel();
const isEmailVerificationDialogVisible = ref(false);
const isPhoneVerificationDialogVisible = ref(false);

const totalEurBalance = computed(() => {
  return ibanAccounts.value.reduce((sum, account) => {
    if (account.currency !== 'EUR') {
      return sum;
    }

    const balance = account.availableBalance ?? account.balances?.availableBalance ?? 0;

    return sum + balance;
  }, 0);
});

const hasBalance = computed(() => isOnboarded.value && totalEurBalance.value > 0);

const isLargeBalance = computed(() => isOnboarded.value && totalEurBalance.value >= 1_000_000);

const isActionDisabled = computed(() => !isOnboarded.value);

const handleOnboardingAction = (key: AccessRequirementKey) => {
  if (key === 'email') {
    if (!isEmailVerificationAvailable.value) {
      return;
    }

    isEmailVerificationDialogVisible.value = true;
    return;
  }

  if (key === 'phone') {
    isPhoneVerificationDialogVisible.value = true;
    return;
  }

  void handleRequirementCta(key);
};

watch(isEmailVerificationAvailable, (isAvailable) => {
  if (!isAvailable) {
    isEmailVerificationDialogVisible.value = false;
  }
});

const handleActionClick = (action: ActionConfig) => {
  if (isActionDisabled.value) {
    return;
  }

  router.push({ name: action.route });
};
</script>

<template>
  <section class="total-balance dots-pattern-2" data-testid="dashboard-total-balance-card">
    <h2 class="total-balance__header">
      <WalletIcon class="total-balance__header-icon" />
      {{ t('totalBalance.title') }}
    </h2>

    <p
      class="total-balance__value"
      :class="{
        'total-balance__value--muted': !hasBalance,
        'total-balance__value--large': isLargeBalance,
      }"
      data-testid="dashboard-total-balance-value"
    >
      {{ hasBalance ? formatWeaverCurrency(totalEurBalance) : '€ 0,00' }}
    </p>

    <div class="total-balance__actions">
      <button
        v-for="action in actions"
        :key="action.id"
        type="button"
        class="total-balance__action"
        :disabled="isActionDisabled"
        @click="handleActionClick(action)"
      >
        <span class="total-balance__action-icon">
          <component :is="action.icon" />
        </span>
        <span class="total-balance__action-label">{{ t(action.labelKey) }}</span>
      </button>
    </div>

    <PromoArea v-if="isOnboarded" />
    <div
      v-else-if="showOnboardingContent && currentOnboardingItem"
      class="total-balance__onboarding"
    >
      <TotalBalanceOnboardingContent
        :current-item="currentOnboardingItem"
        :loading="isVerificationLoading"
        @action="handleOnboardingAction"
      />
    </div>
  </section>
  <TotalBalanceEmailVerificationDialog v-model:visible="isEmailVerificationDialogVisible" />
  <TotalBalancePhoneVerificationDialog v-model:visible="isPhoneVerificationDialogVisible" />
</template>

<style lang="scss" scoped>
.total-balance {
  display: flex;
  flex-direction: column;
  gap: $spacing-xl;
  padding: $spacing-xl;
  border: 1px solid $color-border-idle;
  border-radius: $radius-16;
  background: $color-bg-surface;

  > * {
    position: relative;
    z-index: 1;
  }

  &__header {
    display: flex;
    gap: $spacing-s;
    align-items: center;
    margin: 0;
    font-size: $font-size-md;
    font-weight: $font-weight-semibold;
    line-height: 1;
    letter-spacing: $tracking-body;
    color: $color-text-primary;
  }

  &__header-icon {
    width: $icon-size-lg;
    height: $icon-size-lg;
    color: $color-icon-muted;
  }

  &__value {
    margin: 0;
    font-size: $font-size-3xl;
    font-weight: $font-weight-medium;
    line-height: 1;
    color: $color-text-primary;

    &--muted {
      color: $color-text-muted;
    }

    &--large {
      font-size: $font-size-2xl;
    }
  }

  &__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: $spacing-s;
  }

  &__action {
    display: flex;
    flex-direction: column;
    gap: $spacing-s;
    align-items: center;
    justify-content: center;
    height: 96px;
    overflow: hidden;
    border: none;
    border-radius: $radius-12;
    background-color: $color-bg-element;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
      background-color: $color-bg-subtle;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  &__action-icon {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: var(--p-button-icon-only-width);
    height: var(--p-button-icon-only-width);
    border-radius: $radius-999;
    background-color: $color-button-primary-idle;
    color: $color-icon-white;

    :deep(svg) {
      width: $icon-size-sm;
      height: $icon-size-sm;
    }
  }

  &__action-label {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-align: center;
    font-size: $font-size-md;
    font-weight: $font-weight-medium;
    line-height: $line-height-sm;
    letter-spacing: $tracking-body;
    color: $color-text-primary;
  }

  &__onboarding {
    border-radius: $radius-12;
    background-color: $color-base-contrast;
    padding: $static-space-3;
  }
}
</style>
